import useSWRMutation from "swr/mutation";
import useSWR from "swr";
import { SWRApiError, swrConfigOptions, useFetcher, useMutater } from "./swr";
import { mapRawToPlanningRequest } from "@/mappers/planning";
import { RawPlanningRequest } from "@/types/planning";
import { WaferStatus } from "@/types/measurementRequest";

export const isPlanningKey = (key: unknown): boolean => {
  if (typeof key !== "object" || key === null || !("url" in key)) return false;
  return (
    typeof key.url === "string" && (key.url === "/planning" || key.url.startsWith("/planning?"))
  );
};

export const usePlanning = (waferStatuses?: readonly WaferStatus[]) => {
  const fetcher = useFetcher();
  const params = new URLSearchParams();
  waferStatuses?.forEach((status) => params.append("wafer_statuses", status));
  const query = params.toString();

  const { data = [], ...rest } = useSWR<RawPlanningRequest[], SWRApiError>(
    { url: query ? `/planning?${query}` : "/planning" },
    fetcher,
    { ...swrConfigOptions, keepPreviousData: false },
  );
  return { data: data.map(mapRawToPlanningRequest), ...rest };
};

type Input = {
  requests_to_plan: {
    measurement_request_id: number;
    measurement_system_id: number;
    wafer_id: number;
    started_at?: string;
    completed_at?: string;
  }[];
};

export const useMarkWaferRequestAsScheduled = () => {
  const mutater = useMutater();

  return useSWRMutation<void, SWRApiError, string, Input>(
    "/planning/mark-wafer-request-as-scheduled",
    mutater,
  );
};

export const useMarkWaferRequestAsMeasured = () => {
  const mutater = useMutater();

  return useSWRMutation<void, SWRApiError, string, Input>(
    "/planning/mark-wafer-request-as-measured",
    mutater,
  );
};

type MarkAsToScheduleInput = {
  measurement_request_id: number;
  wafer_id: number;
};

export const useMarkWaferRequestAsToSchedule = () => {
  const mutater = useMutater();

  return useSWRMutation<void, SWRApiError, string, MarkAsToScheduleInput>(
    "/planning/mark-wafer-request-as-to-schedule",
    mutater,
  );
};

type UpdateMeasurementRequestPriorityInput = {
  measurement_request_id: number;
  new_priority: number;
};

export const useUpdateMeasurementRequestPriority = () => {
  const mutater = useMutater();

  return useSWRMutation<void, SWRApiError, string, UpdateMeasurementRequestPriorityInput>(
    "/planning/update-measurement-request-priority",
    mutater,
  );
};

type UpdateWaferRequestPriorityInput = {
  wafer_id: number;
  wafer_id_to_swap_with: number;
};
export const useUpdateWaferRequestPriority = () => {
  const mutater = useMutater();

  return useSWRMutation<void, SWRApiError, string, UpdateWaferRequestPriorityInput>(
    "/planning/update-wafer-request-priority",
    mutater,
  );
};
